import React, { useState } from "react";
import { Card, ConfigProvider, Dropdown, Input, Modal, message } from "antd";
import { IoWarning } from "react-icons/io5";
import { FaCog } from "react-icons/fa";
import { SessionStatus } from "../../constants/enum";
import { useAuth } from "../../context/AuthContext";
import { useSession } from "../../context/SessionContext";
import { useSessionById, useCreateSession, useUpdateSessionStatus, useUpdateSessionName } from "../../api/SessionsAPI";
import { useSessionResponses } from "../../api/SessionResponsesAPI";
import { useCompetenciesCount } from "../../api/CompetenciesAPI";
import { showNotification } from "../common/Notification";
import PhaseSelector from "./PhaseSelector";
import { heroCardTheme } from "../../antdTheme"

const CompetencyRing = ({ value, max }) => {
  const size = 128;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = max > 0 ? Math.min(value / max, 1) : 0;
  const offset = circumference * (1 - progress);

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.15)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#FFCB08"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-3xl font-bold text-white">{value}</span>
      </div>
    </div>
  );
};

const HeroCard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [newSessionName, setNewSessionName] = useState("");
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  const { currentUser } = useAuth();
  const { sessionId, setSessionId } = useSession();
  const { data: sessionInfo } = useSessionById(sessionId);
  const { data: sessionResponses } = useSessionResponses(sessionId);
  const { data: competenciesCount } = useCompetenciesCount();
  const createSessionMutation = useCreateSession();
  const updateSessionStatusMutation = useUpdateSessionStatus();
  const updateSessionNameMutation = useUpdateSessionName();

  // Calculate completed competencies count
  const completedCount = sessionResponses?.filter(response => response.completed)?.length || 0;
  const totalCompetencies = competenciesCount || 0;

  // Only show active individual sessions
  const isActiveIndividual = !!(
    sessionInfo &&
    !sessionInfo.cohort_id &&
    sessionInfo.status === SessionStatus.IN_PROGRESS
  );


  const handleStartNewSession = () => {
    if (!currentUser) {
      showNotification("error", "Error", "Please login to start a new session");
      return;
    }
    setNewSessionName("");
    setIsNameModalOpen(true);
  };

  const createNamedSession = () => {
    console.log('Creating session for user:', currentUser.id);
    setIsLoading(true);
    createSessionMutation.mutate({ userId: currentUser.id, name: newSessionName }, {
      onSuccess: (newSession) => {
        console.log("New session created:", newSession);
        setSessionId(newSession.id);
        setIsNameModalOpen(false);
        showNotification("success", "Success", "New session created successfully!");
      },
      onError: (error) => {
        console.error("Error creating a new session:", error);
        showNotification("error", "Error Creating Session", error.message);
      },
      onSettled: () => {
        setIsLoading(false);
      }
    });
  };

  const completeSession = () => {
    setIsLoading(true);
    updateSessionStatusMutation.mutate(
      { sessionId, status: SessionStatus.COMPLETED },
      {
        onSuccess: () => {
          setSessionId(null);
          showNotification("success", "Success", "Session completed successfully!");
        },
        onError: (error) => {
          console.error("Error completing the session:", error);
          showNotification("error", "Error Completing Session", error.message);
        },
        onSettled: () => {
          setIsLoading(false);
        }
      }
    );
  };

  const handleSessionMenuClick = ({ key }) => {
    if (key === "complete") {
      completeSession();
    } else if (key === "rename") {
      setRenameDraft(sessionInfo?.name || sessionInfo?.display_name || "");
      setIsRenameModalOpen(true);
    }
  };

  const handleRenameSave = () => {
    setIsRenaming(true);
    updateSessionNameMutation.mutate(
      { sessionId: sessionInfo.id, name: renameDraft.trim() },
      {
        onSuccess: () => {
          message.success("Session name updated");
          setIsRenameModalOpen(false);
        },
        onError: (error) => {
          message.error(error.response?.data?.error || "Failed to update session name");
        },
        onSettled: () => {
          setIsRenaming(false);
        }
      }
    );
  };

  if (isLoading || (sessionId && !sessionInfo)) {
    return (
      <ConfigProvider
        theme={heroCardTheme}
      >
        <Card
          title={null}
          className="w-full bg-northern-lights-deep rounded-2xl shadow-md border-0"
          styles={{ body: { padding: "2.5rem" } }}
        >
          <div className="animate-pulse">
            <div className="h-4 bg-white/20 rounded w-1/4 mb-6"></div>
            <div className="h-10 bg-white/20 rounded w-2/3 mb-4"></div>
            <div className="h-4 bg-white/20 rounded w-1/3"></div>
          </div>
        </Card>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={heroCardTheme}>
      <Card
        title={null}
        className="w-full bg-northern-lights-deep rounded-2xl shadow-md border-0 p-2"
      >
        <div className="flex items-center justify-between mb-10">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
            <span className="w-2 h-2 rounded-full bg-accent"></span>
            <span className="text-2xs text-white whitespace-nowrap">Current Session</span>
          </div>

          {isActiveIndividual && (
            <Dropdown
              menu={{
                items: [
                  { key: "rename", label: <span className="text-black">Rename Session</span> },
                  { key: "complete", label: <span className="text-black">Complete Session</span> },
                ],
                onClick: handleSessionMenuClick,
              }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <button
                aria-label="Session options"
                className="flex items-center gap-2 rounded-full bg-white/10 text-white text-2xs font-medium px-4 py-2 hover:bg-white/20"
              >
                <FaCog />
                Settings
              </button>
            </Dropdown>
          )}
        </div>

        <div className="flex items-center justify-between gap-8">
          <div className="min-w-0 flex-1">
            {isActiveIndividual ? (
              <>
                <h1 className="text-2xl font-bold text-white truncate">
                  {sessionInfo.name || sessionInfo.display_name || "Untitled Session"}
                </h1>
                <p className="text-sm text-gray-300 mt-2">
                  {sessionInfo.cohort_id ? "Cohort Session" : "Individual Session"} • Started{" "}
                  {new Date(sessionInfo.created_at).toLocaleDateString()}
                </p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-white">No Active Session</h1>
                <p className="text-sm text-gray-300 mt-2 flex items-center gap-2">
                  <IoWarning /> No individual session available
                </p>
              </>
            )}

            {isActiveIndividual ? null : (
              <div className="mt-6">
                <button
                  onClick={handleStartNewSession}
                  disabled={isLoading}
                  className="px-4 py-2 font-medium bg-accent rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed text-black"
                >
                  {isLoading ? "Creating Session..." : "Start New Session!"}
                </button>
              </div>
            )}
          </div>

          {isActiveIndividual && (
            <div className="flex flex-col items-center shrink-0 mr-8">
              <CompetencyRing value={completedCount} max={totalCompetencies} />
              <p className="text-xs text-gray-300 mt-2 whitespace-nowrap">Competencies</p>
            </div>
          )}
        </div>

        {isActiveIndividual && (
          <div className="mt-8">
            <p className="text-base font-semibold text-white mb-4">Continue working on a phase below</p>
            <PhaseSelector />
          </div>
        )}


        <Modal
          title={<span className="text-gray-900">Name your session</span>}
          open={isNameModalOpen}
          onOk={createNamedSession}
          onCancel={() => setIsNameModalOpen(false)}
          confirmLoading={isLoading}
          okText="Create Session"
          okButtonProps={{ style: { color: "#ffffff" } }}
          cancelButtonProps={{ style: { color: "#111827" } }}
        >
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Session name
          </label>
          <Input
            value={newSessionName}
            onChange={(e) => setNewSessionName(e.target.value)}
            maxLength={100}
            showCount
            placeholder="Untitled Session"
            className="!text-gray-900"
          />
          <p className="text-xs text-gray-500 mt-2">
            You can leave this blank and rename it later.
          </p>
        </Modal>

        <Modal
          title={<span className="text-gray-900">Rename session</span>}
          open={isRenameModalOpen}
          onOk={handleRenameSave}
          onCancel={() => setIsRenameModalOpen(false)}
          confirmLoading={isRenaming}
          okText="Save"
          okButtonProps={{ style: { color: "#ffffff" } }}
          cancelButtonProps={{ style: { color: "#111827" } }}
        >
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Session name
          </label>
          <Input
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            maxLength={100}
            showCount
            placeholder="Untitled Session"
            className="!text-gray-900"
          />
        </Modal>
      </Card>
    </ConfigProvider>
  );
};


export default HeroCard;
